import axios from "axios";

// Bunny.net Stream API Configuration (CRA env vars must start with REACT_APP_)
const BUNNY_API_KEY = process.env.REACT_APP_BUNNY_API_KEY || "";
const LIBRARY_ID = process.env.REACT_APP_BUNNY_LIBRARY_ID || "";
const BUNNY_STREAM_BASE_URL = "https://video.bunnycdn.com";
const BUNNY_CDN_HOST = process.env.REACT_APP_BUNNY_CDN_URL || "";

if (!BUNNY_API_KEY || !LIBRARY_ID) {
  // eslint-disable-next-line no-console
  console.error(
    "Missing Bunny.net API configuration. Please set REACT_APP_BUNNY_API_KEY and REACT_APP_BUNNY_LIBRARY_ID in your .env file"
  );
}

export const bunnyApi = axios.create({
  baseURL: BUNNY_STREAM_BASE_URL,
  headers: {
    AccessKey: BUNNY_API_KEY,
    "Content-Type": "application/json",
  },
});

export const DEFAULT_VIDEO_RESOLUTIONS = "360p,480p,720p,1080p";

const normalizeCdnHost = (host) => {
  const value = String(host || "").trim();
  if (!value) return "";
  return value.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
};

const resolveBunnyCdnHost = (videoLibraryIdOverride) => {
  const configuredHost = normalizeCdnHost(BUNNY_CDN_HOST);
  if (configuredHost) return configuredHost;

  const libId =
    videoLibraryIdOverride != null && String(videoLibraryIdOverride).trim()
      ? String(videoLibraryIdOverride).trim()
      : LIBRARY_ID;

  return libId ? `vz-${libId}.b-cdn.net` : "";
};

export const formatFileSize = (bytes) => {
  const b = Number(bytes || 0);
  if (!Number.isFinite(b) || b <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(b) / Math.log(1024)), units.length - 1);
  const value = b / Math.pow(1024, i);
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

export const formatDuration = (seconds) => {
  const s = Math.max(0, Number(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

// Build direct MP4 URLs for a Bunny video using the CDN host.
// Note: this assumes standard Bunny naming: /{videoId}/play_{resolution}.mp4
export const getCdnMp4Urls = (videoId) => {
  const host = resolveBunnyCdnHost();
  if (!host || !videoId) return {};
  const base = `https://${host}/${videoId}`;
  return {
    "240p": `${base}/play_240p.mp4`,
    "360p": `${base}/play_360p.mp4`,
    "480p": `${base}/play_480p.mp4`,
    "720p": `${base}/play_720p.mp4`,
    "1080p": `${base}/play_1080p.mp4`,
  };
};

/** HLS master playlist URL for apps/backends that expect M3U8 (not the iframe embed URL). */
export const getPlaylistM3u8Url = (videoId) => {
  const host = resolveBunnyCdnHost();
  if (!host || !videoId) return "";
  return `https://${host}/${videoId}/playlist.m3u8`;
};

export const collectionAPI = {
  getAll: async (page = 1, itemsPerPage = 100, search, orderBy) => {
    const params = new URLSearchParams({
      page: String(page),
      itemsPerPage: String(itemsPerPage),
    });
    if (search) params.append("search", search);
    if (orderBy) params.append("orderBy", orderBy);

    const response = await bunnyApi.get(
      `/library/${LIBRARY_ID}/collections?${params.toString()}`
    );
    return response.data;
  },

  getById: async (collectionId) => {
    const response = await bunnyApi.get(
      `/library/${LIBRARY_ID}/collections/${collectionId}`
    );
    return response.data;
  },

  create: async ({ name }) => {
    const response = await bunnyApi.post(`/library/${LIBRARY_ID}/collections`, {
      name,
    });
    return response.data;
  },

  update: async (collectionId, data) => {
    const response = await bunnyApi.post(
      `/library/${LIBRARY_ID}/collections/${collectionId}`,
      data
    );
    return response.data;
  },

  delete: async (collectionId) => {
    await bunnyApi.delete(`/library/${LIBRARY_ID}/collections/${collectionId}`);
  },
};

/** Get a direct CDN MP4 URL for a single resolution (e.g. "480p"). */
export const getCdnMp4Url = (videoId, resolution) => {
  const host = resolveBunnyCdnHost();
  if (!host || !videoId) return "";
  return `https://${host}/${videoId}/play_${resolution}.mp4`;
};

export const videoAPI = {
  getAll: async (page = 1, itemsPerPage = 100, search, collection, orderBy) => {
    const params = new URLSearchParams({
      page: String(page),
      itemsPerPage: String(itemsPerPage),
    });
    if (search) params.append("search", search);
    if (collection) params.append("collection", collection);
    if (orderBy) params.append("orderBy", "title");

    const response = await bunnyApi.get(
      `/library/${LIBRARY_ID}/videos?${params.toString()}`
    );
    return response.data;
  },

  getById: async (videoId) => {
    const response = await bunnyApi.get(`/library/${LIBRARY_ID}/videos/${videoId}`);
    return response.data;
  },

  create: async (data) => {
    const requestData = {
      ...data,
      enabledResolutions: data.enabledResolutions || DEFAULT_VIDEO_RESOLUTIONS,
    };
    const response = await bunnyApi.post(
      `/library/${LIBRARY_ID}/videos`,
      requestData
    );
    return response.data;
  },

  delete: async (videoId) => {
    await bunnyApi.delete(`/library/${LIBRARY_ID}/videos/${videoId}`);
  },

  getPlayerUrl: (videoId) => {
    return `https://iframe.mediadelivery.net/embed/${LIBRARY_ID}/${videoId}`;
  },

  /** Alias for getPlaylistM3u8Url — used by forms to populate the video URL field. */
  getPlaylistUrl: (videoId) => getPlaylistM3u8Url(videoId),

  /** Return a direct CDN MP4 URL for one resolution (e.g. "480p"). */
  getMp4Url: (videoId, resolution) => getCdnMp4Url(videoId, resolution),

  /** Return a map of resolution → CDN MP4 URL (same as getCdnMp4Urls). */
  getMp4UrlMap: (videoId) => getCdnMp4Urls(videoId),

  getThumbnailUrl: (
    videoId,
    thumbnailFileName = "thumbnail.jpg",
    videoLibraryIdOverride
  ) => {
    const host = resolveBunnyCdnHost(videoLibraryIdOverride);
    if (!host || !videoId) return "";

    const safeName =
      typeof thumbnailFileName === "string" && thumbnailFileName.trim()
        ? thumbnailFileName.trim()
        : "thumbnail.jpg";
    return `https://${host}/${videoId}/${encodeURIComponent(safeName)}`;
  },
};

export const uploadVideoWithTus = async ({
  file,
  videoId,
  onProgress,
  onSuccess,
  onError,
}) => {
  const { Upload } = await import("tus-js-client");

  if (!BUNNY_API_KEY || !LIBRARY_ID) {
    const error = new Error("Bunny.net API credentials are missing");
    onError?.(error);
    throw error;
  }

  return new Promise((resolve, reject) => {
    const upload = new Upload(file, {
      endpoint: "https://video.bunnycdn.com/tusupload",
      retryDelays: [0, 3000, 5000, 10000, 20000],
      headers: {
        AccessKey: BUNNY_API_KEY,
        VideoId: videoId,
        LibraryId: LIBRARY_ID,
      },
      metadata: {
        filetype: file.type,
        title: file.name,
      },
      fingerprint: (f) => Promise.resolve(`${videoId}-${f.name}-${f.size}`),
      removeFingerprintOnSuccess: true,
      onError: (error) => {
        // eslint-disable-next-line no-console
        console.error("TUS upload error:", error);
        onError?.(error);
        reject(error);
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        const percentage = Math.round((bytesUploaded / bytesTotal) * 100);
        onProgress?.(percentage);
      },
      onSuccess: () => {
        onSuccess?.();
        resolve();
      },
    });

    upload.start();
  });
};

export const getVideoStatusText = (status) => {
  switch (status) {
    case 0:
      return "Created";
    case 1:
      return "Uploaded";
    case 2:
      return "Processing";
    case 3:
      return "Transcoding";
    case 4:
      return "Finished";
    case 5:
      return "Error";
    case 6:
      return "Upload Failed";
    default:
      return "Unknown";
  }
};

export const getVideoStatusColor = (status) => {
  switch (status) {
    case 1:
      return "blue";
    case 2:
    case 3:
      return "orange";
    case 4:
      return "green";
    case 5:
    case 6:
      return "red";
    default:
      return "default";
  }
};

export { BUNNY_API_KEY, LIBRARY_ID };

